import React, { useState, useEffect } from 'react';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import { 
  ContentCopy, 
  ContentPaste, 
  ContentCut,
  Delete,
  Add,
  Remove,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight
} from '@mui/icons-material';
import './ContextMenu.css';

const ContextMenu = () => {
  const {
    getActiveSheet,
    insertRow,
    deleteRow,
    insertColumn,
    deleteColumn,
    copySelection,
    pasteSelection,
    pasteFromClipboard,
    updateCellFormat
  } = useSpreadsheetStore();

  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [context, setContext] = useState('cell'); // 'cell', 'row', 'column'

  const activeSheet = getActiveSheet();
  const { activeCell } = activeSheet;

  // Context menu göster
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      
      // Grid alanında mı kontrol et
      const gridElement = e.target.closest('.spreadsheet-grid');
      if (!gridElement) return;

      const rect = gridElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Hangi alanda olduğunu belirle
      let contextType = 'cell';
      if (y < 30) contextType = 'column'; // Sütun başlığı
      else if (x < 60) contextType = 'row'; // Satır başlığı

      setContext(contextType);
      setPosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
    };

    // Context menu kapat
    const handleClick = () => {
      setIsVisible(false);
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Menü pozisyonu
  const menuStyle = {
    position: 'fixed',
    top: position.y,
    left: position.x,
    zIndex: 1000
  };

  // Satır işlemleri
  const handleInsertRow = () => {
    insertRow(activeCell.row);
    setIsVisible(false);
  };

  const handleDeleteRow = () => {
    deleteRow(activeCell.row);
    setIsVisible(false);
  };

  // Sütun işlemleri
  const handleInsertColumn = () => {
    insertColumn(activeCell.col);
    setIsVisible(false);
  };

  const handleDeleteColumn = () => {
    deleteColumn(activeCell.col);
    setIsVisible(false);
  };

  // Kopyala/Yapıştır
  const handleCopy = () => {
    copySelection();
    setIsVisible(false);
  };

  const handlePaste = async () => {
    // Önce dış verilerden yapıştırmayı dene
    try {
      await pasteFromClipboard(activeCell.row, activeCell.col);
    } catch (error) {
      // Dış veri yoksa, dahili clipboard'ı kullan
      pasteSelection(activeCell.row, activeCell.col);
    }
    setIsVisible(false);
  };

  // Biçimlendirme
  const handleFormatChange = (format) => {
    updateCellFormat(activeCell.row, activeCell.col, format);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="context-menu" style={menuStyle}>
      {context === 'cell' && (
        <>
          <div className="context-menu-section">
            <div className="context-menu-item" onClick={handleCopy}>
              <ContentCopy fontSize="small" />
              <span>Kopyala</span>
            </div>
            <div className="context-menu-item" onClick={handlePaste}>
              <ContentPaste fontSize="small" />
              <span>Yapıştır</span>
            </div>
            <div className="context-menu-item">
              <ContentCut fontSize="small" />
              <span>Kes</span>
            </div>
            <div className="context-menu-item">
              <Delete fontSize="small" />
              <span>Sil</span>
            </div>
          </div>

          <div className="context-menu-separator"></div>

          <div className="context-menu-section">
            <div className="context-menu-item" onClick={() => handleFormatChange({ bold: true })}>
              <FormatBold fontSize="small" />
              <span>Kalın</span>
            </div>
            <div className="context-menu-item" onClick={() => handleFormatChange({ italic: true })}>
              <FormatItalic fontSize="small" />
              <span>İtalik</span>
            </div>
            <div className="context-menu-item" onClick={() => handleFormatChange({ underline: true })}>
              <FormatUnderlined fontSize="small" />
              <span>Altı Çizili</span>
            </div>
          </div>

          <div className="context-menu-separator"></div>

          <div className="context-menu-section">
            <div className="context-menu-item" onClick={() => handleFormatChange({ horizontalAlign: 'left' })}>
              <FormatAlignLeft fontSize="small" />
              <span>Sola Hizala</span>
            </div>
            <div className="context-menu-item" onClick={() => handleFormatChange({ horizontalAlign: 'center' })}>
              <FormatAlignCenter fontSize="small" />
              <span>Ortala</span>
            </div>
            <div className="context-menu-item" onClick={() => handleFormatChange({ horizontalAlign: 'right' })}>
              <FormatAlignRight fontSize="small" />
              <span>Sağa Hizala</span>
            </div>
          </div>
        </>
      )}

      {context === 'row' && (
        <>
          <div className="context-menu-section">
            <div className="context-menu-item" onClick={handleInsertRow}>
              <Add fontSize="small" />
              <span>Satır Ekle</span>
            </div>
            <div className="context-menu-item" onClick={handleDeleteRow}>
              <Remove fontSize="small" />
              <span>Satır Sil</span>
            </div>
          </div>
        </>
      )}

      {context === 'column' && (
        <>
          <div className="context-menu-section">
            <div className="context-menu-item" onClick={handleInsertColumn}>
              <Add fontSize="small" />
              <span>Sütun Ekle</span>
            </div>
            <div className="context-menu-item" onClick={handleDeleteColumn}>
              <Remove fontSize="small" />
              <span>Sütun Sil</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContextMenu;
